// Import package components
import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import { Image, Flex } from "uikit-react";
import UIkit from "uikit";
import { useCookies } from "react-cookie";
import classnames from "classnames";
import Papa from "papaparse";
import { useLocation, useHistory } from "react-router-dom";

// Import base components
import { AppContexts } from "../../providers";

// Import common components
import PageTitle from "../common/PageTitle";
import Pagination from "../common/Pagination";
import RecordsPerPage from "../common/RecordsPerPage";
import TableLoading from "../common/Loading/table_loading";
import { Link } from "../../ui";
import AddTextLine from "../common/AddTextLine";

// Import contact components
import ContactAdd from "./add";
import ContactDelete from "./delete";
import Message from "./message";
import Tags from "../common/Tags";
import Properties from "../common/Properties";
import TagsList from "../common/Rentals/TagsList";
import NoteAdd from "../notes/note-add";
import SearchableDropdown from "../common/SearchableDropdown";
import { useDeepCompareEffect } from "../../hook";
import { usePermissionsStore } from "../permissions/permissions_store";
import { tagFilterOptions } from "../../utils/tag-groups";

const sortOptions = [
  { key: "updated_at", title: "Last Updated" },
  { key: "first_name", title: "First Name" },
  { key: "last_name", title: "Last Name" },
];

const contactTypeLabels = {
  owner: "Owner",
  housekeeper: "Housekeeper",
  maintenance: "Maintenance",
  vendor: "Vendor",
  leads: "Lead",
  other: "Other",
};

const CONTACT_IMPORT_CATEGORIES = new Set([
  "owner",
  "maintenance",
  "vendor",
  "other",
  "leads",
  "housekeeper",
]);

const defaultCriteria = {
  search_term: "",
  sort: "updated_at",
  tag: "",
  tag_group: "all",
  type: "",
  property: "",
  per: "20",
  page: 0,
};

const parseQueryString = (queryString) => {
  return {
    ...defaultCriteria,
    ...Object.fromEntries(new URLSearchParams(queryString)),
  };
};

const Contacts = () => {
  const location = useLocation();
  const history = useHistory();
  const [criteria, setCriteria] = useState(() => {
    return location.search
      ? parseQueryString(location.search)
      : defaultCriteria;
  });
  const [contactFilterDraft, setContactFilterDraft] = useState(() => ({
    type: criteria.type || "",
    property: criteria.property || "",
    tag: criteria.tag || "",
    tag_group: criteria.tag_group || "all",
  }));
  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((previous) => ({
      ...previous,
      [key]: value,
      page: key === "page" ? value : 0,
    }));
  }, []);
  const [selectedFlags, setSelectedFlags] = useState([]);

  const contactContext = useContext(AppContexts.ContactsContext);
  const settingsContext = useContext(AppContexts.SettingsContext);
  const rentalContext = useContext(AppContexts.RentalsContext);
  // const notesContext = useContext(AppContexts.NotesContext);
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;
  const { canEdit } = usePermissionsStore();
  const canManageContacts = canEdit("messaging.contacts");
  const canMessageContacts = canEdit("messaging.contacts_message");

  const inputRef = useRef(null);
  const filterDropdownRef = useRef(null);

  const { listContacts, totalCnt, flagIsProcessing, updateContact } =
    contactContext;
  const contacts = useMemo(
    () => (listContacts?.length > recordsPerPage ? [] : listContacts || []),
    [listContacts],
  );

  useEffect(() => {
    document.title = "Contacts - RueBaRue";
    settingsContext.get(["tags"]);
    rentalContext.get();
  }, []);

  useEffect(() => {
    setCriteria((it) => ({ ...it, per: recordsPerPage }));
  }, [recordsPerPage]);

  useEffect(() => {
    setContactFilterDraft({
      type: criteria.type || "",
      property: criteria.property || "",
      tag: criteria.tag || "",
      tag_group: criteria.tag_group || "all",
    });
  }, [
    criteria.type,
    criteria.property,
    criteria.tag,
    criteria.tag_group,
  ]);

  useDeepCompareEffect(() => {
    const searchParams = new URLSearchParams(criteria).toString();
    history.replace(`${location.pathname}?${searchParams}`);
  }, [criteria]);

  useDeepCompareEffect(() => {
    let timeoutId;

    timeoutId = setTimeout(() => contactContext.searchContacts(criteria), 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [criteria]);

  useEffect(() => {
    if (selectedFlags.length !== contacts.length)
      setSelectedFlags(Array(contacts.length).fill(false));
  }, [contacts.length, selectedFlags.length]);

  useEffect(() => {
    contactContext.setSelectedContacts(
      selectedFlags
        .map((selected, index) => (selected ? contacts[index] : null))
        .filter((contact) => contact),
    );
  }, [selectedFlags, contacts]);

  const propertyValues = useMemo(
    () =>
      (rentalContext.rentals || [])
        .filter((r) => r.pms_id)
        .sort((a, b) => a.pms_id.localeCompare(b.pms_id)) || [],
    [rentalContext.rentals],
  );
  const tagValues = useMemo(
    () =>
      settingsContext.settings.tags?.sort((a, b) =>
        a.name.localeCompare(b.name),
      ) || [],
    [settingsContext.settings],
  );
  const tagGroups = useMemo(
    () => settingsContext.settings?.tag_groups || [],
    [settingsContext.settings?.tag_groups],
  );
  const availableTagOptions = useMemo(
    () =>
      tagFilterOptions(
        tagValues,
        tagGroups,
        criteria.tag_group,
        (tag) => tag.name,
      ),
    [tagValues, tagGroups, criteria.tag_group],
  );
  const draftTagOptions = useMemo(
    () =>
      tagFilterOptions(
        tagValues,
        tagGroups,
        contactFilterDraft.tag_group,
        (tag) => tag.name,
      ),
    [tagValues, tagGroups, contactFilterDraft.tag_group],
  );

  useEffect(() => {
    if (
      !settingsContext.loading &&
      settingsContext.settings?.tags &&
      criteria.tag &&
      !availableTagOptions.some((option) => option.value === criteria.tag)
    ) {
      handleCriteriaChange("tag", "");
    }
  }, [
    availableTagOptions,
    criteria.tag,
    handleCriteriaChange,
    settingsContext.loading,
    settingsContext.settings?.tags,
  ]);

  const { selectedAll, selectedAny } = useMemo(() => {
    const hasFlags = selectedFlags.length > 0;
    const selectedAll = hasFlags && selectedFlags.every((checked) => checked);
    const selectedAny = hasFlags && selectedFlags.some((checked) => checked);

    return { selectedAll, selectedAny };
  }, [selectedFlags]);

  const handleAddContact = useCallback(() => {
    contactContext.setIsNew(true);
    contactContext.initContact();
  }, []);

  const handleEditContact = useCallback((contact) => {
    contactContext.setIsNew(false);
    contactContext.setContact(contact);
  });

  const handleSelected = useCallback(() => {
    contactContext.setBatchFlag(true);
  }, []);

  const handleSelectedTags = useCallback((tags, contact) => {
    let temp = { ...contact, tags };
    updateContact(temp);
  }, []);

  const handleSelectedRentals = useCallback((rentals, contact) => {
    let temp = { ...contact, rentals };
    updateContact(temp);
  }, []);

  const handleMessage = useCallback((contact) => {
    contactContext.setBatchFlag(false);
    contactContext.setContact(contact);
  }, []);

  const handleDeleteContact = useCallback((contact) => {
    contactContext.setBatchFlag(false);
    contactContext.setContact(contact);
  });

  const applyContactFilters = () => {
    setCriteria((previous) => ({
      ...previous,
      ...contactFilterDraft,
      page: 0,
    }));
    if (filterDropdownRef.current) {
      UIkit.dropdown(filterDropdownRef.current).hide(false);
    }
  };

  const clearContactFilters = () => {
    setContactFilterDraft({
      type: "",
      property: "",
      tag: "",
      tag_group: "all",
    });
  };

  const removeAppliedContactFilter = (key) => {
    setCriteria((previous) => ({
      ...previous,
      [key]: key === "tag_group" ? "all" : "",
      ...(key === "tag_group" && { tag: "" }),
      page: 0,
    }));
  };

  const clearAppliedContactFilters = () => {
    setCriteria((previous) => ({
      ...previous,
      type: "",
      property: "",
      tag: "",
      tag_group: "all",
      page: 0,
    }));
  };

  const hasAppliedContactFilters =
    criteria.type ||
    criteria.property ||
    criteria.tag ||
    (criteria.tag_group && criteria.tag_group !== "all");
  const handleIndexChanged = useCallback((newPageIndex) => {
    setSelectedFlags([]);
    handleCriteriaChange("page", newPageIndex);
  });

  const handleSelect = useCallback((checked, index) => {
    setSelectedFlags((flags) => [
      ...flags.slice(0, index),
      checked,
      ...flags.slice(index + 1),
    ]);
  }, []);

  // const handleAddNote = (contact) => {
  //   notesContext.getNoteByItem(contact.id, 'contact');
  //   contactContext.setContact(contact);
  // }

  const contactRenderer = useCallback(
    (contact, index) => (
      <tr key={contact?.id}>
        {canManageContacts && (
          <td>
            <div className="autosend-checkbox">
              <label htmlFor={`autoSendCheckbox${index}`}>
                <input
                  type="checkbox"
                  id={`autoSendCheckbox${index}`}
                  checked={selectedFlags[index]}
                  onChange={(event) => handleSelect(event.target.checked, index)}
                />
              </label>
            </div>
          </td>
        )}
        <td>
          <div className="contact-td">
            <span
              uk-tooltip={`title: ${contact?.category
                .toLowerCase()
                .replace(/\b\w/g, (s) =>
                  s.toUpperCase(),
                )}; pos: bottom-top; cls: uk-active contact-tooltip`}
              style={{ width: "32px", height: "32px" }}
            >
              <img
                src={`/images/${contact?.category.toLowerCase()}-active.svg`}
                alt="active.svg"
                width="30px"
                height="30px"
              />
            </span>
            <div className="hms-guests-column">
              <div className="semi-bold-font">
                {contact?.first_name} {contact?.last_name}
              </div>
              <div className="secondary">{contact?.formatted_phone}</div>
              <div className="secondary">{contact?.email}</div>
              <div className="hms-booking-tag uk-margin-small-top">
                {contact?.category === "leads" ? "lead" : contact.category}
              </div>
            </div>
          </div>
        </td>
        <td className="hms-add-tag">
          <Properties
            value={contact?.rentals}
            onChange={(editedTags) =>
              handleSelectedRentals(editedTags, contact)
            }
            readOnly={!canManageContacts}
          />
        </td>
        <td className="hms-add-tag">
          <Tags
            all={tagValues}
            value={contact?.tags}
            onChange={(event) => handleSelectedTags(event, contact)}
            readOnly={!canManageContacts}
          />
        </td>
        {/*
        <td>
          <div className="uk-flex uk-flex-top">
            <div className="hms-add-tag">
              <button className="uk-button" type="button" aria-haspopup="true" aria-expanded="false"><img src="/images/group.svg" /></button>
              <div data-uk-dropdown="mode: click" className="guest-tags-dropdown uk-dropdown">
                <ul className="uk-nav uk-dropdown-nav tags-selector">
                  <li>
                    <label className="disabled"><input className="uk-checkbox" type="checkbox" />Florida - Homes</label>
                  </li>
                  <li>
                    <label className="disabled"><input className="uk-checkbox" type="checkbox" checked />Georgia - Homes</label>
                  </li>
                  <li>
                    <label className=""><input className="uk-checkbox" type="checkbox" />Georgia - Homes</label>
                  </li>
                </ul>
              </div>
            </div>
            <div className="hms-guest-tags">
              <div className="guest-tags">
                <div className="uk-inline zone">
                  Group 1 - Beachside Condos
                </div>
                <div className="uk-inline zone">
                  Group 2 - Gardenside Condos
                </div>
                <div className="uk-inline zone">
                  Contact - Owners
                </div>
                <div className="uk-inline zone">
                  Contact - Leads (Chatbot)
                </div>                                    
              </div>
            </div>             
          </div>
        </td>
        */}
        <td className="hms-action-btn">
          <div className="uk-flex uk-flex-top">
            <div className="uk-inline">
              {canMessageContacts && (
                <button
                  className="uk-button"
                  uk-toggle="target: #modal-contact-message"
                  onClick={() => handleMessage(contact)}
                  uk-tooltip="Send a Message"
                >
                  <Image src="/images/sms.svg" />
                </button>
              )}
              {/* <button
                className="uk-button"
                uk-toggle="target: #modal-guest-note"
                onClick={() => handleAddNote(contact)}
                uk-tooltip="Notes"
              >
                {
                  contact?.notes?.length > 0
                  ? (<Image src="/images/notes-filled.svg" />)
                  : (<Image src="/images/notes.svg" />)
                }
              </button> */}
              {canManageContacts && (
                <button
                  className="uk-button"
                  uk-toggle="target: #modal-contact-add"
                  onClick={() => handleEditContact(contact)}
                  uk-tooltip="Edit"
                >
                  <Image src="/images/edit.svg" />
                </button>
              )}
            </div>
            {canManageContacts && (
              <div className="uk-inline">
                <button
                  className="uk-button"
                  uk-toggle="target: #modal-contact-delete"
                  onClick={() => handleDeleteContact(contact)}
                  uk-tooltip="Delete"
                >
                  <Image src="/images/delete.svg" />
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>
    ),
    [canManageContacts, canMessageContacts, selectedFlags],
  );

  const handleSelectAll = useCallback(
    (event) =>
      setSelectedFlags((flags) => flags.map(() => event.target.checked)),
    [selectedFlags],
  );

  const handleImport = async (event) => {
    const fileObj = event.target.files && event.target.files[0];
    if (!fileObj) {
      return;
    }
    // Store the current input value
    Papa.parse(fileObj, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let data = results.data.map((row) => {
          const category = row.category?.trim().toLowerCase();
          return {
            ...row,
            category: CONTACT_IMPORT_CATEGORIES.has(category)
              ? category
              : "other",
          };
        });

        // Check if there's any data
        if (!data.length) {
          UIkit.notification("No content", "error");
          return;
        }

        // Validate required columns
        const columns = Object.keys(data[0]);
        let requiredColumns = [
          "category",
          "email",
          "first_name",
          "last_name",
          "phone",
        ];
        let missingColumns = requiredColumns.filter(
          (col) => !columns.includes(col),
        );

        if (missingColumns.length) {
          UIkit.notification(
            `Missing Columns: ${missingColumns.join(", ")}`,
            "error",
          );
          return;
        }

        // Split the data into chunks, e.g., 100 rows per chunk
        let chunkSize = 10; // Adjust chunk size according to your API limits
        let chunks = [];
        for (let i = 0; i < data.length; i += chunkSize) {
          chunks.push(data.slice(i, i + chunkSize));
        }

        try {
          // Create promises for each chunk import
          const promises = chunks.map((chunk) =>
            contactContext.importContacts(chunk),
          );

          // Wait for all promises to resolve using Promise.all
          const results = await Promise.all(promises);

          // Process results to calculate duplicates and errors
          let totalDuplicates = 0;
          let totalErrors = [];

          results.forEach((result) => {
            totalDuplicates += result?.exists?.length || 0;
            if (result?.errors?.length) {
              totalErrors = totalErrors.concat(result.errors);
            }
          });

          // Display notifications based on results
          if (totalDuplicates > 0) {
            UIkit.notification(
              `${totalDuplicates} records already exist.`,
              "error",
            );
          }

          if (totalErrors.length > 0) {
            UIkit.notification(
              `Failed: ${totalErrors.length} records.`,
              "error",
            );
          } else if (totalDuplicates === 0 && totalErrors.length === 0) {
            UIkit.notification("Contacts Added Successfully", "success");
          }

          // Refresh the contact list after the import is done
          await contactContext.searchContacts(criteria);
        } catch (error) {
          // Handle any error that occurs during the chunked imports
          console.error("Error importing contacts:", error);
          UIkit.notification(
            "Error importing contacts. Please try again.",
            "error",
          );
        }

        // Reset input field and trigger input event
        event.target.value = "";
        const newEvent = new Event("input", { bubbles: true });
        event.target.dispatchEvent(newEvent);
      },
    });
  };

  const handleClickImport = () => {
    inputRef.current.click();
  };

  return (
    <>
      <section id="view-add-contacts">
        <AddTextLine feature="Contacts" />
        <PageTitle title="Contacts" />

        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              {canManageContacts && (
                <button
                  className="uk-button hms-btn hms-red-btn"
                  uk-toggle="target: #modal-contact-add"
                  onClick={handleAddContact}
                >
                  <span uk-icon="icon: plus; ratio: 0.7"></span> Contact
                </button>
              )}
              {canMessageContacts && (
                <button
                  className="uk-button hms-btn hms-orange-btn"
                  uk-toggle="target: #modal-contact-message"
                  disabled={!selectedAny}
                  onClick={handleSelected}
                >
                  <span uk-icon="icon: mail; ratio: 1"></span> Message
                </button>
              )}
              {canManageContacts && (
                <TagsList
                  all={tagValues}
                  disabled={!selectedAny || !canManageContacts}
                  selected={contactContext.selectedContacts}
                  updateItem={updateContact}
                  buttonClassName="uk-button hms-btn hms-white-btn crm-icon-btn"
                />
              )}
              <input
                style={{ display: "none" }}
                ref={inputRef}
                type="file"
                onChange={handleImport}
                accept=".csv"
              />
              {canManageContacts && (
                <button
                  className="uk-button hms-btn hms-white-btn"
                  onClick={handleClickImport}
                >
                  <span uk-icon="icon: arrow-up; ratio: 1"></span> Import
                </button>
              )}
              {canManageContacts && (
                <a
                  className="uk-margin-small-right"
                  href="/samples/contact-import-sample.csv"
                  style={{ color: "black" }}
                  target="_blank"
                >
                  <img
                    className="info-icon"
                    src="/images/info-icon.svg"
                    uk-tooltip="Click to download sample comma separated file (CSV) to bulk import contacts"
                  />
                </a>
              )}
              {canManageContacts && (
                <button
                  className="uk-button hms-btn hms-white-btn"
                  uk-toggle="target: #modal-contact-delete"
                  disabled={!selectedAny}
                  onClick={handleSelected}
                >
                  <span uk-icon="icon: trash; ratio: 0.7"></span> Delete
                </button>
              )}
            </div>
          </div>
          <div className="uk-card uk-card-small uk-card-default hms-form-card">
            <div className="uk-overflow-auto">
              <form action="" className="uk-form">
                <Flex
                  alignment="between top"
                  className="guest-form-top-filters"
                >
                  <div className="hms-form contacts-filter-form">
                    <div className="uk-inline hms-search-inline">
                      <div className="uk-search uk-search-default">
                        {criteria.search_term ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() =>
                              handleCriteriaChange("search_term", "")
                            }
                          ></a>
                        ) : (
                          <span
                            className="uk-search-icon-flip"
                            uk-search-icon="true"
                          />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Name, Property ID"
                          value={criteria.search_term}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                          onChange={(e) =>
                            handleCriteriaChange("search_term", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="uk-inline hms-filters-btn guest-more-filters">
                      <button
                        className="filter guest-more-filters-button"
                        type="button"
                        aria-label="Filter contacts"
                      >
                        <img
                          src="/images/filter.svg"
                          alt="Filter"
                          uk-tooltip="Filter"
                        />
                      </button>
                      <div
                        ref={filterDropdownRef}
                        uk-dropdown="mode: click; offset: 4; animation: uk-animation-slide-top-small; duration: 200"
                        className="hms-filters-dropdown guest-more-filters-dropdown"
                      >
                        <div className="uk-form-stacked">
                          <div className="uk-margin-small">
                            <label className="uk-form-label">Type</label>
                            <div className="uk-form-controls">
                              <select
                                className="uk-select"
                                value={contactFilterDraft.type}
                                onChange={(event) =>
                                  setContactFilterDraft((prev) => ({
                                    ...prev,
                                    type: event.target.value,
                                  }))
                                }
                              >
                                <option value="">All Types</option>
                                <option value="owner">Owner</option>
                                <option value="housekeeper">Housekeeper</option>
                                <option value="maintenance">Maintenance</option>
                                <option value="vendor">Vendor</option>
                                <option value="leads">Lead</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                          </div>
                          {!(settingsContext.loading || rentalContext.loading) && (
                            <>
                              <div className="uk-margin-small">
                                <label className="uk-form-label">Property</label>
                                <div className="uk-form-controls">
                                  <SearchableDropdown
                                    inline
                                    value={contactFilterDraft.property}
                                    onChange={(val) =>
                                      setContactFilterDraft((prev) => ({
                                        ...prev,
                                        property: val,
                                      }))
                                    }
                                    placeholder="All Properties"
                                    options={[
                                      { value: "", label: "All Properties" },
                                      ...propertyValues.map((property) => ({
                                        value: property.pms_id,
                                        label: property.pms_id,
                                      })),
                                    ]}
                                  />
                                </div>
                              </div>
                              {tagGroups.length > 0 && (
                                <div className="uk-margin-small">
                                  <label className="uk-form-label">
                                    Tag Group
                                  </label>
                                  <div className="uk-form-controls">
                                    <SearchableDropdown
                                      inline
                                      value={contactFilterDraft.tag_group}
                                      onChange={(val) =>
                                        setContactFilterDraft((prev) => ({
                                          ...prev,
                                          tag_group: val,
                                          tag: "",
                                        }))
                                      }
                                      placeholder="All Tag Groups"
                                      options={[
                                        {
                                          value: "all",
                                          label: "All Tag Groups",
                                        },
                                        ...tagGroups.map((group) => ({
                                          value: String(group.id),
                                          label: group.label,
                                        })),
                                      ]}
                                    />
                                  </div>
                                </div>
                              )}
                              <div className="uk-margin-small">
                                <label className="uk-form-label">Tags</label>
                                <div className="uk-form-controls">
                                  <SearchableDropdown
                                    inline
                                    value={contactFilterDraft.tag}
                                    onChange={(val) =>
                                      setContactFilterDraft((prev) => ({
                                        ...prev,
                                        tag: val,
                                      }))
                                    }
                                    placeholder="All Tags"
                                    options={[
                                      { value: "", label: "All Tags" },
                                      ...draftTagOptions,
                                    ]}
                                  />
                                </div>
                              </div>
                            </>
                          )}
                          <div className="hms-filter-actions uk-margin-small-top">
                            <button
                              type="button"
                              className="uk-button hms-btn hms-red-btn"
                              onClick={applyContactFilters}
                            >
                              Apply
                            </button>
                            <button
                              type="button"
                              className="uk-button hms-btn hms-white-btn"
                              onClick={clearContactFilters}
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="hms-sortby-btn">
                      <button className="uk-button" type="button">
                        SORT BY<span uk-icon="icon: triangle-down"></span>
                      </button>
                      <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                        <ul className="uk-nav uk-dropdown-nav">
                          {sortOptions.map((option, idx) => (
                            <li
                              className={classnames({
                                "uk-active": criteria.sort === option.key,
                              })}
                              key={`sort_option_${idx}`}
                            >
                              <Link
                                href="#"
                                onClick={() =>
                                  handleCriteriaChange("sort", option.key)
                                }
                              >
                                {option.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Flex>
                {hasAppliedContactFilters && (
                  <div className="hms-active-filters uk-margin-small-top uk-margin-small-bottom">
                    <span className="hms-active-filters-label">Filters:</span>
                    {criteria.type && (
                      <span className="hms-filter-chip">
                        Type: {contactTypeLabels[criteria.type] || criteria.type}
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() => removeAppliedContactFilter("type")}
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {criteria.property && (
                      <span className="hms-filter-chip">
                        Property: {criteria.property}
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() => removeAppliedContactFilter("property")}
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {criteria.tag && (
                      <span className="hms-filter-chip">
                        Tag: {criteria.tag}
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() => removeAppliedContactFilter("tag")}
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {criteria.tag_group &&
                      criteria.tag_group !== "all" && (
                        <span className="hms-filter-chip">
                          Tag Group:{" "}
                          {tagGroups.find(
                            (group) =>
                              String(group.id) ===
                              String(criteria.tag_group),
                          )?.label || criteria.tag_group}
                          <button
                            type="button"
                            className="hms-filter-chip-close"
                            onClick={() =>
                              removeAppliedContactFilter("tag_group")
                            }
                          >
                            ×
                          </button>
                        </span>
                      )}
                    <button
                      type="button"
                      className="hms-filter-chip-clear-all"
                      onClick={clearAppliedContactFilters}
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </form>
              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    {canManageContacts && (
                      <th className="uk-table-shrink">
                        <label
                          htmlFor="checkboxContactAll"
                          className="checkboxContact"
                        >
                          <input
                            type="checkbox"
                            id="checkboxContactAll"
                            checked={selectedAll}
                            onChange={handleSelectAll}
                          />
                        </label>
                      </th>
                    )}
                    <th className="uk-table-expand name-field">
                      Name/Phone/Email
                    </th>
                    <th className="uk-table-expand">Properties</th>
                    <th className="uk-table-expand">Tags</th>
                    {/*
                    <th className="uk-table-expand">
                      User Groups
                      <img 
                        className="info-icon" src="/images/info-icon.svg"
                        uk-tooltip="Restrict access to specific contacts and messages." />
                    </th>
                    */}
                    <th className="uk-width-205 actions">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {flagIsProcessing == true ? (
                    TableLoading(6)
                  ) : contacts.length > 0 ? (
                    contacts.map(contactRenderer)
                  ) : (
                    <tr>
                      <td colSpan={9} style={{ padding: "30px 25px" }}>
                        No contacts matching your current search or filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination
              count={totalCnt}
              index={Number(criteria.page)}
              onIndexChanged={handleIndexChanged}
            />
            {totalCnt > 0 && <RecordsPerPage />}
          </Flex>
        </div>
      </section>
      {canMessageContacts && <Message />}
      <ContactAdd />
      <ContactDelete />
      <NoteAdd itemId={contactContext.contact.id} itemType="contact" />
    </>
  );
};

export default Contacts;
